import { useNavigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, formatMinutes } from "@/lib/format";
import { useDashboard } from "@/contexts/dashboard-context";
import { Calendar, Clock, Car, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAnimatedValue } from "@/hooks/use-animated-value";

export function TodaySummary() {
  const navigate = useNavigate();
  const { todaySummary, isLoading } = useDashboard();

  // Animated values for smooth transitions
  const animatedEarnings = useAnimatedValue(todaySummary.totalEarnings);
  const animatedMiles = useAnimatedValue(todaySummary.totalMiles);
  const animatedHourlyRate = useAnimatedValue(
    todaySummary.actualHourlyRate ?? todaySummary.avgHourlyRate
  );
  const animatedPerMileRate = useAnimatedValue(todaySummary.avgPerMileRate);

  if (isLoading) {
    return <TodaySummarySkeleton />;
  }

  if (todaySummary.blockCount === 0) {
    return (
      <Card
        className="cursor-pointer hover:bg-accent hover:border-accent transition-colors"
        onClick={() => navigate("/daily")}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">No blocks today yet</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const bookedDuration = formatMinutes(todaySummary.totalMinutes);
  const actualDuration = todaySummary.actualMinutes
    ? formatMinutes(todaySummary.actualMinutes)
    : null;

  return (
    <Card
      className="cursor-pointer hover:bg-accent hover:border-accent transition-colors"
      onClick={() => navigate("/daily")}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Today</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {todaySummary.blockCount} block{todaySummary.blockCount !== 1 ? "s" : ""}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">
              {formatCurrency(Math.round(animatedEarnings))}
            </p>
            <p className="text-xs text-muted-foreground">earnings</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold">
              {animatedMiles.toFixed(1)} mi
            </p>
            <p className="text-xs text-muted-foreground">
              {actualDuration || bookedDuration}
              {actualDuration && (
                <span className="text-muted-foreground/60 ml-1">
                  ({bookedDuration} booked)
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex justify-between text-sm border-t pt-2">
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span>
              {formatCurrency(Math.round(animatedHourlyRate))}/hr
              {todaySummary.actualHourlyRate && (
                <span className="text-xs text-muted-foreground/60 ml-1">actual</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Car className="h-3.5 w-3.5 text-muted-foreground" />
            <span>
              {formatCurrency(Math.round(animatedPerMileRate * 100) / 100)}/mi
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TodaySummarySkeleton() {
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-12" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Skeleton className="h-8 w-24 mb-1" />
            <Skeleton className="h-3 w-14" />
          </div>
          <div className="flex flex-col items-end">
            <Skeleton className="h-6 w-16 mb-1" />
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
        <div className="flex justify-between border-t pt-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardContent>
    </Card>
  );
}
